// [PURPOSE]: Off-List Scroll Casting Arcana Check Modal (Sub-plan 4M — 2026-05-28)
// [WHY]: DMG p.139 house rule — when a player attempts to cast a spell scroll for a
//   spell NOT on their class list, they must make an Intelligence (Arcana) check
//   (DC 10 + spell level). Success → cast proceeds. Failure → scroll consumed + fizzle.
// [INTEGRATION]: Opened by App.jsx readScrollSpell when enableOffListCasting=true +
//   isSpellOnClassList() returns false. onConfirm receives { rolled, total, dc, success }.
//   onCancel → parent preserves scroll.
// [RULE 19]: useModalEscape applied for keyboard accessibility.

import React, { useState } from 'react';
import { Z } from '../constants/zLayers';
import { useModalEscape } from '../hooks/useModalEscape';
import { getOffListArcanaDC } from '../utils/offListScrollCheck';

/**
 * Off-List Scroll Cast — Arcana Check modal.
 *
 * @param {boolean}  isOpen    - Whether the modal is visible.
 * @param {Object}   spell     - The spell being cast (must have .name and .level).
 * @param {number}   arcanaMod - The character's current Arcana modifier (INT mod + proficiency if applicable).
 * @param {Function} onConfirm - Called with { rolled, total, dc, success } after dice roll.
 * @param {Function} onCancel  - Called when the player cancels (no cast, no consume).
 */
const OffListScrollCastModal = ({ isOpen, spell, arcanaMod = 0, onConfirm, onCancel }) => {
  // [RULE 19]: Escape key dismisses modal (treats as cancel — no cast, no consume).
  useModalEscape(isOpen, onCancel);

  const [rollResult, setRollResult] = useState(null); // null | { rolled, total, dc, success }
  const [hasRolled, setHasRolled] = useState(false);

  if (!isOpen || !spell) return null;

  const dc = getOffListArcanaDC(spell);
  const spellName = spell.name || 'Unknown Spell';
  const spellLevel = (typeof spell.level === 'number' ? spell.level : parseInt(spell.level, 10)) || 0;
  const levelLabel = spellLevel === 0 ? 'cantrip' : `level ${spellLevel}`;

  const handleRoll = () => {
    const rolled = Math.floor(Math.random() * 20) + 1;
    const total = rolled + arcanaMod;
    const success = total >= dc;
    const result = { rolled, total, dc, success };
    setRollResult(result);
    setHasRolled(true);
    // Brief delay so the player sees the result before the modal closes
    setTimeout(() => {
      onConfirm(result);
      // Reset for reuse
      setRollResult(null);
      setHasRolled(false);
    }, 1500);
  };

  const handleCancel = () => {
    setRollResult(null);
    setHasRolled(false);
    onCancel();
  };

  const modLabel = arcanaMod >= 0 ? `+${arcanaMod}` : `${arcanaMod}`;

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center p-4"
      style={{ zIndex: Z.MODAL }}
      data-testid="off-list-scroll-modal"
    >
      <div className="bg-gray-900 border border-purple-700/60 rounded-xl shadow-2xl max-w-sm w-full p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <span className="text-2xl">📜</span>
          <div>
            <h2 className="text-lg font-bold text-purple-200">Off-List Scroll Cast</h2>
            <p className="text-xs text-gray-400">Intelligence (Arcana) Check</p>
          </div>
        </div>

        {/* Spell info */}
        <div className="bg-gray-800/60 rounded-lg p-3 mb-4 border border-gray-700/50">
          <p className="text-white font-semibold">{spellName}</p>
          <p className="text-xs text-gray-400 capitalize">{levelLabel}</p>
        </div>

        {/* Description */}
        <p className="text-sm text-gray-300 mb-4">
          <span className="font-semibold text-amber-300">{spellName}</span> is not on your class list.
          Make an Intelligence (Arcana) check vs <span className="font-bold text-white">DC {dc}</span>.
          {' '}On a failure, the scroll is destroyed and the spell fizzles.
        </p>

        {/* DC and modifier breakdown */}
        <div className="flex gap-3 mb-5 text-center">
          <div className="flex-1 bg-gray-800 rounded-lg p-2 border border-gray-700/50">
            <p className="text-xs text-gray-400 mb-1">DC</p>
            <p className="text-xl font-bold text-white">{dc}</p>
            <p className="text-xs text-gray-500">10 + level {spellLevel}</p>
          </div>
          <div className="flex-1 bg-gray-800 rounded-lg p-2 border border-gray-700/50">
            <p className="text-xs text-gray-400 mb-1">Arcana Mod</p>
            <p className={`text-xl font-bold ${arcanaMod >= 0 ? 'text-green-400' : 'text-red-400'}`}>{modLabel}</p>
            <p className="text-xs text-gray-500">INT + prof</p>
          </div>
        </div>

        {/* Roll result display */}
        {hasRolled && rollResult && (
          <div
            className={`rounded-lg p-3 mb-4 text-center border ${
              rollResult.success
                ? 'bg-green-900/40 border-green-600/60'
                : 'bg-red-900/40 border-red-600/60'
            }`}
            data-testid="roll-result"
          >
            <p className="text-xs text-gray-300 mb-1">
              d20 ({rollResult.rolled}) {arcanaMod >= 0 ? `+${arcanaMod}` : arcanaMod} = <span className="font-bold text-white text-lg">{rollResult.total}</span>
            </p>
            <p className={`font-bold text-sm ${rollResult.success ? 'text-green-300' : 'text-red-300'}`}>
              {rollResult.success ? '✅ Success — spell casts!' : '❌ Failure — scroll consumed!'}
            </p>
          </div>
        )}

        {/* Action buttons */}
        {!hasRolled && (
          <div className="flex gap-3">
            <button
              onClick={handleRoll}
              className="flex-1 bg-purple-700 hover:bg-purple-600 text-white font-bold py-2 px-4 rounded-lg transition-colors"
              data-testid="roll-button"
            >
              🎲 Roll d20
            </button>
            <button
              onClick={handleCancel}
              className="flex-1 bg-gray-700 hover:bg-gray-600 text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              data-testid="cancel-button"
            >
              Cancel
            </button>
          </div>
        )}

        {/* Rule footnote */}
        <p className="text-xs text-gray-600 mt-3 text-center">
          DMG p.139 house rule • Default RAW: scroll unintelligible
        </p>
      </div>
    </div>
  );
};

export default OffListScrollCastModal;
